"use client"

import { useEffect, useId, useMemo } from "react"
import { pluralize } from "@/utils/text-helpers"
import { zodResolver } from "@hookform/resolvers/zod"
import { SelectInput } from "@ui/forms"
import { Form, FormField } from "@ui/shad"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { useFetchCurrentUser } from "@/hooks/api/users/queries"
import { useBookId } from "@/hooks/use-book-id"
import { SimpleFormDialog } from "@/components/dialogs"

const moveToBookSchema = z.object({
  destination_book_id: z.string().min(1, "Destination book is required"),
})

export type MoveToBookFormValues = z.infer<typeof moveToBookSchema>

interface MoveToBookDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (destinationBookId: string) => void
  isSubmitting: boolean
  selectedHouseholdCount: number
  entityType: "client" | "prospect" | "past_client" | "other contact"
}

export function MoveToBookDialog({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
  selectedHouseholdCount,
  entityType,
}: MoveToBookDialogProps) {
  const dialogFormId = useId()

  const form = useForm<MoveToBookFormValues>({
    resolver: zodResolver(moveToBookSchema),
    defaultValues: {
      destination_book_id: "",
    },
  })

  const currentBookId = useBookId()
  const { data: currentUser } = useFetchCurrentUser()

  useEffect(() => {
    if (!open) {
      form.reset({ destination_book_id: "" })
    }
  }, [form, open])

  const bookOptions = useMemo(() => {
    if (!currentUser?.books) return []
    return currentUser.books
      .filter((book) => book.id !== currentBookId)
      .map((book) => ({
        value: book.id,
        label: book.name,
      }))
  }, [currentUser?.books, currentBookId])

  const destinationBookId = form.watch("destination_book_id")

  const isConfirmDisabled = isSubmitting || bookOptions.length === 0 || !destinationBookId

  const handleOpenChange = (nextOpen: boolean) => {
    if (isSubmitting && !nextOpen) return
    onOpenChange(nextOpen)
  }

  const handleSubmit = form.handleSubmit((values) => {
    onConfirm(values.destination_book_id)
  })

  return (
    <SimpleFormDialog
      title={<>Move {pluralize(selectedHouseholdCount, entityType)} to another book</>}
      open={open}
      onOpenChange={handleOpenChange}
      action={{
        label: <>Move {pluralize(selectedHouseholdCount, entityType)}</>,
        form: `${dialogFormId}-dialog-move-to-book-dialog-88`,
        disabled: isConfirmDisabled,
        pending: isSubmitting,
      }}
      cancel={{ label: "Cancel", onClick: () => handleOpenChange(false), disabled: isSubmitting }}
    >
      <Form {...form}>
        <form
          id={`${dialogFormId}-dialog-move-to-book-dialog-88`}
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col gap-2"
        >
          <>
            <FormField
              control={form.control}
              name="destination_book_id"
              render={({ field }) => (
                <SelectInput
                  id="destination-book-selector"
                  label="Destination book"
                  value={field.value}
                  onChange={(value: string | null) => field.onChange(value ?? "")}
                  options={bookOptions}
                  placeholder={bookOptions.length === 0 ? "No other books available" : "Select a book"}
                  disabled={bookOptions.length === 0}
                />
              )}
            />

            <p className="text-muted-foreground text-sm">
              This will move the selected {pluralize(selectedHouseholdCount, entityType)} and all associated data to the
              selected book. This action runs in the background and may take a few moments.
            </p>
          </>
        </form>
      </Form>
    </SimpleFormDialog>
  )
}
